import { API_URL } from '../config';
import { getHomeRouteByRole } from './roles';
import { normalizeInvitePaymentRedirect } from './invitePaymentFlow';

const SESSION_STATUS_CACHE_MS = 5000;
let sessionStatusCache = null;
let sessionStatusInflight = null;

/**
 * @typedef {{ ok?: boolean; usuario?: { rol?: string; id?: string | number } | null }} VerifyTokenPayload
 */

/**
 * @param {string | URLSearchParams | undefined | null} search
 * @returns {string | null}
 */
export function resolveSafeRedirectUrl(search) {
  const params = search instanceof URLSearchParams
    ? search
    : new URLSearchParams(typeof search === 'string' ? search : '');

  let redirectUrl = params.get('redirect');
  if (!redirectUrl) {
    return null;
  }

  if (redirectUrl.startsWith('/') && !redirectUrl.startsWith('//')) {
    const orphanOrderId = params.get('orderId')?.trim();
    if (
      orphanOrderId &&
      !redirectUrl.includes('orderId=') &&
      (redirectUrl === '/checkout' ||
        redirectUrl === '/invitar-pago' ||
        redirectUrl.startsWith('/checkout?') ||
        redirectUrl.startsWith('/invitar-pago?'))
    ) {
      const hashIndex = redirectUrl.indexOf('#');
      const pathWithoutHash = hashIndex === -1 ? redirectUrl : redirectUrl.slice(0, hashIndex);
      const hashSuffix = hashIndex === -1 ? '' : redirectUrl.slice(hashIndex);
      const separator = pathWithoutHash.includes('?') ? '&' : '?';
      redirectUrl = `${pathWithoutHash}${separator}orderId=${encodeURIComponent(orphanOrderId)}${hashSuffix}`;
    }
    return normalizeInvitePaymentRedirect(redirectUrl);
  }

  try {
    const url = new URL(redirectUrl, window.location.origin);
    if (url.origin === window.location.origin) {
      const path = `${url.pathname}${url.search}${url.hash}`;
      return normalizeInvitePaymentRedirect(path);
    }
  } catch {
    return null;
  }

  return null;
}

/**
 * @param {string | undefined | null} rol
 * @param {string | URLSearchParams | undefined | null} search
 * @returns {string}
 */
export function resolvePostLoginPath(rol, search) {
  const safeRedirectUrl = resolveSafeRedirectUrl(search);
  if (safeRedirectUrl) {
    return normalizeStudentCourseRedirect(rol, safeRedirectUrl);
  }
  return getHomeRouteByRole(rol);
}

/**
 * A fresh student session must reopen the purchased course shell, not an
 * assessment-only screen that depends on navigation state from the old
 * session. Keep the course navigation query so tab/block/topic context is
 * restored by Campus when it is available.
 */
export function normalizeStudentCourseRedirect(rol, redirectUrl) {
  if (rol !== 'alumno' || typeof redirectUrl !== 'string') {
    return redirectUrl;
  }

  const match = redirectUrl.match(
    /^(\/usuario\/(?:test-examen|test-temas))(\?[^#]*)?(#.*)?$/,
  );
  if (!match) {
    return redirectUrl;
  }

  return `/usuario/campus${match[2] || ''}${match[3] || ''}`;
}

function clearStoredUserId() {
  try {
    localStorage.removeItem('idUsuario');
  } catch {
    /* ignore storage access errors */
  }
}

/**
 * Persists non-sensitive client hints returned by /login.
 * Session credentials stay in httpOnly cookies only.
 * Storage failures must not block post-login navigation.
 *
 * @param {{ id?: string | number | null }} data
 */
export function persistLoginSession(data) {
  if (data?.id == null) {
    clearStoredUserId();
    return;
  }

  try {
    localStorage.setItem('idUsuario', String(data.id));
    if (data?.activeCourseId != null && String(data.activeCourseId).trim()) {
      localStorage.setItem('activeCourseId', String(data.activeCourseId));
    }
  } catch {
    /* ignore storage access errors */
  }
}

/**
 * Keeps lightweight client hints aligned with a verified server session.
 *
 * @param {{ id?: string | number | null; rol?: string | null }} usuario
 */
export function syncClientSessionHints(usuario) {
  if (usuario?.id == null) {
    clearStoredUserId();
    return;
  }

  try {
    localStorage.setItem('idUsuario', String(usuario.id));
  } catch {
    /* ignore storage access errors */
  }
}

/**
 * @param {AbortSignal | undefined} signal
 * @param {{ force?: boolean }} [options]
 * @returns {Promise<{ authenticated: boolean; usuario: VerifyTokenPayload['usuario'] | null }>}
 */
export async function fetchSessionStatus(signal, options = {}) {
  if (signal?.aborted) {
    throw new DOMException('Aborted', 'AbortError');
  }

  const now = Date.now();
  if (!options.force && sessionStatusCache && sessionStatusCache.expiresAt > now) {
    return sessionStatusCache.value;
  }

  if (!options.force && sessionStatusInflight) {
    const sharedResult = await sessionStatusInflight;
    if (signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError');
    }
    return sharedResult;
  }

  sessionStatusInflight = (async () => {
    const response = await fetch(`${API_URL}/verificar-token`, {
      method: 'GET',
      credentials: 'include',
    });

    if (!response.ok) {
      return { authenticated: false, usuario: null };
    }

    /** @type {VerifyTokenPayload} */
    const data = await response.json();
    if (data.ok !== true) {
      return { authenticated: false, usuario: null };
    }

    if (data.usuario) {
      syncClientSessionHints(data.usuario);
    }

    return {
      authenticated: true,
      usuario: data.usuario ?? null,
    };
  })();

  try {
    const result = await sessionStatusInflight;
    sessionStatusCache = {
      value: result,
      expiresAt: Date.now() + SESSION_STATUS_CACHE_MS,
    };
    if (signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError');
    }
    return result;
  } finally {
    sessionStatusInflight = null;
  }
}

export function clearSessionStatusCache() {
  sessionStatusCache = null;
  sessionStatusInflight = null;
}
